import { useMemo, type ReactNode } from 'react';
import {
  LuChevronDown,
  LuChevronLeft,
  LuChevronRight,
  LuChevronUp,
  LuChevronsLeft,
  LuChevronsRight,
  LuLoaderCircle,
} from 'react-icons/lu';
import clsx from 'clsx';
import {
  columnVisibilityFeature,
  createExpandedRowModel,
  flexRender,
  rowExpandingFeature,
  tableFeatures,
  useTable,
  type ColumnDef,
  type ExpandedState,
} from '@tanstack/react-table';

// v9 は使う機能を明示登録する。coreRowModel は自動なので登録不要 (react-table v9 移行)
const features = tableFeatures({
  columnVisibilityFeature,
  rowExpandingFeature,
  expandedRowModel: createExpandedRowModel(),
});
import type { AgentApiAnalysisSharedTimeSeries } from '@/types/serializers';
import { TABLE_STEPS, type TableStepDefinition } from '../constants';
import AnalysisNoData from './AnalysisNoData';

interface AnalysisDailyTableProps {
  /** APIレスポンスの時系列データ */
  timeSeries: AgentApiAnalysisSharedTimeSeries;
  /** 表示中のステップキー */
  visibleSteps: string[];
  /** グループ選択有無（グループ有りの場合は展開行を表示） */
  isGrouped: boolean;
  /** 現在ページ */
  currentPage: number;
  /** ページ変更コールバック */
  onPageChange: (page: number) => void;
  /** 再フェッチ中（ページング等で前データを保持しながら裏側で更新中） */
  isFetching?: boolean;
}

/**
 * バケット文字列を表示用にフォーマットする
 * 例: "2026-04-13" → "2026/04/13" / "2026-W17" → "2026/W17" / "2026-04" → "2026/04"
 */
function formatBucket(bucket: string): string {
  if (/^\d{4}-W\d{2}$/.test(bucket)) {
    return bucket.replace('-', '/');
  }
  if (/^\d{4}-\d{2}$/.test(bucket)) {
    return bucket.replace('-', '/');
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(bucket)) {
    return bucket.replaceAll('-', '/');
  }
  return bucket;
}

/** グループキー（"user:山田 太郎" 等）から表示ラベルを抽出する */
function formatGroupKey(key: string): string {
  const colonIdx = key.indexOf(':');
  return colonIdx !== -1 ? key.slice(colonIdx + 1) : key;
}

/** TanStack Table 向けの行データ */
type AnalysisRow = {
  /** 行種別を `id` で識別する（subRow は `${bucket}---${groupKey}` 形式） */
  id: string;
  /** 親行の場合は日付バケット表示用の文字列 */
  bucket?: string;
  /** subRow の場合のグループキー */
  groupKey?: string;
  /** ステップごとの値（key=stepKey） */
  counts: Record<string, number>;
  /** 子行（グループ展開時のみ） */
  subRows?: AnalysisRow[];
};

/** API レスポンスを TanStack Table 用の階層構造に変換する */
function buildTableData(
  timeSeries: AgentApiAnalysisSharedTimeSeries,
  isGrouped: boolean,
  displayTableSteps: TableStepDefinition[]
): AnalysisRow[] {
  return timeSeries.rows.map((row) => {
    // 親行は API ステップキー単位で値を取得（無ければ groupKey___apiKey の合算）
    const aggregatedCounts = displayTableSteps.reduce<Record<string, number>>((acc, ts) => {
      const direct = row.counts[ts.key];
      acc[ts.key] =
        direct ??
        Object.entries(row.counts).reduce(
          (sum, [k, v]) => (k.endsWith(`___${ts.key}`) ? sum + v : sum),
          0
        );
      return acc;
    }, {});

    if (!isGrouped) {
      return { id: row.bucket, bucket: row.bucket, counts: aggregatedCounts };
    }

    // グループキーを抽出して subRow に展開
    const groupKeys = Array.from(
      new Set(
        Object.keys(row.counts)
          .filter((k) => k.includes('___'))
          .map((k) => k.split('___')[0])
      )
    );

    const subRows: AnalysisRow[] = groupKeys.map((groupKey) => ({
      id: `${row.bucket}---${groupKey}`,
      groupKey,
      counts: displayTableSteps.reduce<Record<string, number>>((acc, ts) => {
        acc[ts.key] = row.counts[`${groupKey}___${ts.key}`] ?? 0;
        return acc;
      }, {}),
    }));

    return {
      id: row.bucket,
      bucket: row.bucket,
      counts: aggregatedCounts,
      subRows: subRows.length > 0 ? subRows : undefined,
    };
  });
}

/**
 * 分析画面の日別テーブルコンポーネント
 * グループ選択時は行クリックで担当者別内訳を展開表示する
 */
export default function AnalysisDailyTable({
  timeSeries,
  visibleSteps,
  isGrouped,
  currentPage,
  onPageChange,
  isFetching = false,
}: AnalysisDailyTableProps) {
  // 表示する API ステップ詳細（visibleSteps は API ステップキーの配列）
  const displayTableSteps = useMemo(
    () => TABLE_STEPS.filter((ts) => visibleSteps.includes(ts.key)),
    [visibleSteps]
  );

  // 行データを TanStack Table 用に変換
  const tableData = useMemo(
    () => buildTableData(timeSeries, isGrouped, displayTableSteps),
    [timeSeries, isGrouped, displayTableSteps]
  );

  // 列定義: 日付列 + ステップ列
  const columns = useMemo<ColumnDef<typeof features, AnalysisRow>[]>(
    () => [
      {
        id: 'bucket',
        header: '日付',
        cell: ({ row }) => {
          const isParent = row.depth === 0;
          const canExpand = row.getCanExpand();
          return (
            <div
              className="flex items-center gap-1 whitespace-nowrap"
              style={{ paddingLeft: isParent ? 0 : 20 }}
            >
              <span className={isParent ? 'text-main' : ''}>
                {isParent
                  ? formatBucket(row.original.bucket ?? '')
                  : formatGroupKey(row.original.groupKey ?? '')}
              </span>
              {canExpand && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    row.getToggleExpandedHandler()();
                  }}
                  className="text-gray-400 hover:text-main"
                  aria-label={row.getIsExpanded() ? '折りたたむ' : '展開する'}
                >
                  {row.getIsExpanded() ? (
                    <LuChevronUp className="size-4" />
                  ) : (
                    <LuChevronDown className="size-4" />
                  )}
                </button>
              )}
            </div>
          );
        },
        meta: { headerClassName: 'w-[130px] text-left' },
      },
      ...displayTableSteps.map<ColumnDef<typeof features, AnalysisRow>>((ts) => {
        // 6文字以上のラベル（例: オファー面談）は 80px 幅に収まらず見切れるため、列幅を個別に広げる
        const widthClassName = ts.label.length >= 6 ? 'w-[110px]' : 'w-[80px]';
        return {
          id: ts.key,
          accessorFn: (row) => row.counts[ts.key] ?? 0,
          header: ts.label,
          cell: ({ getValue }) => {
            const value = getValue() as number;
            // 値 >=1 のセルは main 色で強調、0 はデフォルトの sub 色のまま
            return (
              <span className={value >= 1 ? 'font-medium text-main' : ''}>
                {value.toLocaleString()}
              </span>
            );
          },
          meta: {
            // 各ステップ列を固定幅にし、はみ出る分は横スクロールに任せる
            cellClassName: 'text-center tabular-nums',
            headerClassName: `${widthClassName} text-center`,
          },
        };
      }),
    ],
    [displayTableSteps]
  );

  // 親行のみ自動で展開
  const initialExpanded: ExpandedState = useMemo(() => ({}), []);

  const table = useTable({
    features,
    data: tableData,
    columns,
    initialState: { expanded: initialExpanded },
    getSubRows: (row) => row.subRows,
  });

  if (timeSeries.rows.length === 0) {
    return <AnalysisNoData />;
  }

  const pagination = timeSeries.total_pages > 1 && (
    <div className="flex items-center justify-end gap-2">
      <p className="flex items-baseline gap-1 text-main">
        <span className="text-lg">{timeSeries.total_count.toLocaleString()}</span>
        <span className="text-sm">件中</span>
        <span className="text-lg">{timeSeries.rows.length.toLocaleString()}</span>
        <span className="text-sm">件</span>
      </p>
      <PageIconButton
        ariaLabel="最初のページへ"
        disabled={timeSeries.is_first_page}
        onClick={() => onPageChange(1)}
      >
        <LuChevronsLeft className="size-5" />
      </PageIconButton>
      <PageIconButton
        ariaLabel="前のページへ"
        disabled={timeSeries.is_first_page}
        onClick={() => onPageChange(currentPage - 1)}
      >
        <LuChevronLeft className="size-5" />
      </PageIconButton>
      {Array.from({ length: Math.min(timeSeries.total_pages, 3) }, (_, i) => {
        const startPage = Math.max(1, Math.min(currentPage - 1, timeSeries.total_pages - 2));
        const page = startPage + i;
        const isActive = page === currentPage;
        return (
          <button
            key={page}
            type="button"
            onClick={() => onPageChange(page)}
            aria-current={isActive ? 'page' : undefined}
            className={clsx(
              'flex size-[42px] items-center justify-center rounded text-sm font-bold',
              isActive
                ? 'bg-primary text-white'
                : 'border border-solid border-border bg-white text-main hover:bg-gray-50'
            )}
          >
            {page}
          </button>
        );
      })}
      <PageIconButton
        ariaLabel="次のページへ"
        disabled={timeSeries.is_last_page}
        onClick={() => onPageChange(currentPage + 1)}
      >
        <LuChevronRight className="size-5" />
      </PageIconButton>
      <PageIconButton
        ariaLabel="最後のページへ"
        disabled={timeSeries.is_last_page}
        onClick={() => onPageChange(timeSeries.total_pages)}
      >
        <LuChevronsRight className="size-5" />
      </PageIconButton>
    </div>
  );

  const rows = table.getRowModel().rows;

  return (
    <div className="relative">
      {/*
       * overflow-clip: border-radius のクリッピングのみ行い、スクロールコンテナは作らない
       * 内側の overflow-auto div が実際のスクロールコンテナ
       * これにより <th> の sticky が内側コンテナのスクロールに追従する
       */}
      <div className="overflow-clip rounded-md border border-solid border-border">
        <div className="overflow-auto" style={{ maxHeight: 'calc(100dvh - 126px)' }}>
          <table className="w-full table-fixed border-collapse text-sm">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="bg-[#f6f6f6]">
                  {headerGroup.headers.map((header) => {
                    const meta = header.column.columnDef.meta as
                      { headerClassName?: string } | undefined;
                    return (
                      <th
                        key={header.id}
                        className={clsx(
                          'sticky top-0 z-10 bg-[#f6f6f6]',
                          'border-b border-solid border-border',
                          'px-4 py-3 text-left text-sm font-bold whitespace-nowrap text-sub',
                          meta?.headerClassName
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={table.getAllLeafColumns().length}
                    className="px-3 py-8 text-center text-sm text-sub"
                  >
                    データがありません
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const isSubRow = row.depth > 0;
                  return (
                    <tr
                      key={row.id}
                      className={clsx(
                        'border-b border-solid border-border bg-white text-sm text-sub',
                        isSubRow && 'bg-[#fafafa] text-xs'
                      )}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const meta = cell.column.columnDef.meta as
                          { cellClassName?: string } | undefined;
                        return (
                          <td
                            key={cell.id}
                            className={clsx(
                              isSubRow ? 'px-4 py-1.5' : 'px-4 py-3',
                              meta?.cellClassName
                            )}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {pagination && (
          <div className="border-t border-solid border-border bg-white px-3 py-2">{pagination}</div>
        )}
      </div>
      {/* ページング等で再フェッチ中はテーブル内をオーバーレイ表示 */}
      {isFetching && (
        <div
          aria-busy="true"
          className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-md bg-white/60"
        >
          <LuLoaderCircle className="size-6 animate-spin text-primary" />
        </div>
      )}
    </div>
  );
}

/** ページネーション用のアイコンボタン（最初/前/次/最後） */
function PageIconButton({
  children,
  onClick,
  disabled,
  ariaLabel,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className="flex size-[42px] items-center justify-center rounded border border-solid border-border bg-white text-sub hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {children}
    </button>
  );
}
